import { useContext, useEffect, useState } from 'react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getAnalyticsApiErrorProps } from '@/analytics/utils/analytics-helpers'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'
import { AuthContext } from '@/context/auth-context'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { postImportFolder } from '@/user_files/external-storage/fetch/post-import-folder'
import type { ImportFolderResponse } from '@/user_files/external-storage/schemas'
import { ExternalStorageActions, selectExternalStorageAccountsRequest, selectVisibleConnectedExternalAccount } from '@/user_files/external-storage/store/slice'
import type { ExternalFolderSelection } from '@/user_files/external-storage/types'

export function useCreateMatterFolderSync(enabled: boolean) {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const { userAccountData } = useContext(AuthContext)
  const cachedAccount = useAppSelector(selectVisibleConnectedExternalAccount)
  const accountsRequest = useAppSelector(selectExternalStorageAccountsRequest)
  const connectedAccount = accountsRequest.status === 'error' ? null : cachedAccount
  const [selection, setSelection] = useState<ExternalFolderSelection | null>(null)
  // A disconnected or replaced account must never leave an invisible pending selection.
  const selectedFolder = enabled && selection?.accountId === connectedAccount?.id ? selection : null

  useEffect(() => {
    if (enabled) dispatch(ExternalStorageActions.fetchConnectedAccounts())
  }, [dispatch, enabled])

  const connectFolder = async (matterId: string): Promise<boolean> => {
    if (!selectedFolder || !connectedAccount) return true

    let response: ImportFolderResponse | null = null
    let failure: unknown
    try {
      response = await postImportFolder({ accountId: connectedAccount.id, externalFolderId: selectedFolder.folderId, matterId })
    } catch (error) {
      failure = error
    }
    // The request can commit even when its response is lost. Reconcile on either outcome.
    dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping({ matterId, force: true }))

    const analyticsProps = {
      matterId,
      organizationId: getDriveSyncOrganizationId(userAccountData),
      externalStorageProvider: connectedAccount.provider,
      replaceExistingMapping: false,
      matterDriveSyncEntrySource: MatterDriveSyncEntrySource.MatterCreation,
    }
    try {
      if (response) {
        trackMatterDriveSyncEvent(
          AnalyticsEvent.MatterDriveSyncFolderMapped,
          {
            ...analyticsProps,
            externalSyncMappingId: response.external_sync_mapping_id,
            createdMapping: response.created_mapping,
            observedFilesCount: response.observed_files_count,
            createdFileLinksCount: response.created_file_links_count,
            observedFoldersCount: response.observed_folders_count,
          },
          trackEvent
        )
      } else {
        trackMatterDriveSyncEvent(AnalyticsEvent.MatterDriveSyncFolderMapFailed, { ...analyticsProps, ...getAnalyticsApiErrorProps(failure) }, trackEvent)
      }
    } catch {
      // Analytics delivery cannot change the saved matter or folder-connection outcome.
    }
    return response != null
  }

  return {
    connectedAccount,
    accountsRequest,
    selectedFolder,
    setSelection,
    connectFolder,
  }
}
